// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * The tour (owner decisions, 2026-10-03 and 2026-10-04). One click starts the ready-made
 * Accounts Payable job on five clean documents, one of each type, and one card at the top
 * of the documents list walks the real workspace in ten steps. Each card's heading is the
 * part of the screen it is about; one paragraph names what glows; one line says what to do.
 * A pulsing orange ring settles on that thing (walking from one to the next when a step names
 * several), and the step moves on when the person uses it or presses Next. Back shows the step before. There is no Skip: Next always
 * moves on. Leaving, or making their own job, removes the tour's job.
 *
 * The same tour runs on the person's own first job when they say Show me around (owner,
 * 2026-10-04: "why is the demo just not the demo for everyone?"). Their real counts, their
 * waiting documents, and an Export step that sets up export rather than assuming a folder;
 * leaving or finishing only ends the tour (OWN_STEPS lists every difference).
 */

import { hostHas } from '../shared/host-capabilities';
import type { ReaderProblem } from './not-read';

export const TOUR_CORPUS = 'accounts_payable_small_set';
/** One of each type, the hardest of each by what is on the page; clean scans (2026-10-03). */
export const TOUR_DOCUMENTS = [
  '094_invoice_summit_APD2-094.tif',
  '017_purchase_order_meadow_APD2-017.tif',
  '020_credit_memo_cedar_valley_APD2-020.tif',
  '021_w9_maple_harbor_APD2-021.tif',
  '026_expense_report_northstar_APD2-026.tif',
];

export type TourStepId =
  | 'pane'
  | 'starts'
  | 'add'
  | 'read'
  | 'confirm'
  | 'review'
  | 'review-wait'
  | 'export'
  | 'files'
  | 'turn';

/**
 * What the card can count on: how many documents wait in Needs review right now, how many the
 * pane holds, the words on the export button the Export step glows on (so the card names
 * exactly that button), and on their own job how many its Export step saw go out, and whether
 * they went to a folder.
 */
export type TourContext = {
  waiting: number;
  total?: number;
  exportLabel?: string;
  exported?: number;
  folder?: boolean;
  /** Calibration is on for the job (the tag beside its name): it asks about more, with no counts. */
  calibrating?: boolean;
  /** The words on the document's one main button right now ("Confirm & next", "Next", "Confirm"). */
  confirmLabel?: string;
};

export type TourStep = {
  /** The card's heading: the part of the screen the step is about. */
  section: string;
  /** One paragraph. `**words**` are the product's own names, in bold. */
  say: (context: TourContext) => string;
  /** What to do, in one line (bold the same way). */
  todo?: (context: TourContext) => string;
  /** The real controls the glow settles on, in order; it walks to each and rests on the last. */
  targets?: string[];
  /** How long the ring stays on each control before walking to the next, in ms (1700 unless a step says). */
  walkEvery?: number;
  /**
   * The walk is part of what the step shows, in the order the words name the controls, so reduced
   * motion keeps it (the ring moves without gliding) instead of resting on the last one at once.
   */
  inTurn?: boolean;
  /**
   * For a control the documents list may not have drawn yet (it draws only the pages near the
   * screen): a mark the list keeps where it will be, which the walk scrolls to, by the control's selector.
   */
  spots?: Record<string, string>;
  /**
   * A control the tour uses once when the step opens: the All tab, so the pane shows all five;
   * Needs review again at Export, so the list lets go of the document just confirmed and shows Export.
   */
  select?: string;
  /**
   * Clicking one of these moves the tour on, `advanceAfter` once the click has done its work (a
   * menu or window it opened is closed, nothing it started is still busy). Unless a step says
   * otherwise it is the controls the ring is on, on steps with Next; null: no click moves it on.
   */
  advanceOn?: string | null;
  /** How long after the click (or after `done` turns true) the tour moves on, in ms (ADVANCE_AFTER unless a step says). */
  advanceAfter?: number;
  /** False where Back would show words that are no longer true (the sample's Export, once done). */
  back?: boolean;
  /**
   * Next on the card. Not on the last step, not on Export (the tour exports only when they
   * click it, so the steps after it stay true), and not while documents wait in Needs review:
   * the tour's export happens on a click, so anything left waiting would never follow.
   */
  next: boolean;
  /** The scissors and paperclip between pages and documents, normally shown only on pointing, stay shown. */
  showStarts?: boolean;
  /** The step moves on by itself once this is true. */
  done?: (context: TourContext) => boolean;
  /** A button on the card itself: open what was just exported (their own job's Your files). */
  openExport?: boolean;
  /** PAIGE: a button on the card itself that downloads the tour's documents (Paige's Your files). */
  download?: boolean;
  /** The step waits for this to appear; until then the card says `whileWaiting`. */
  waitFor?: string;
  whileWaiting?: string;
};

/** Beside the job's name at the top of the list: the review count, or the Calibration tag (one list since 2026-10-30). */
const COUNT = '.spine-review-count, .spine-calibration-tag';
/** A field's value box on the right. */
const FIELD_VALUE = '[data-review-pane="fields"] .field-review-value';
/** A moment after a click (or a count reaching 0) before the tour moves on, so they see what it did. */
export const ADVANCE_AFTER = 1000;
// Needs review, option 2 (owner, 2026-10-04): start with the list, short sentences. "A job can be
// set to ask for your review…" read as clumsy and ran long. Second sentence added 2026-10-05.
const REVIEW_IS =
  "**Needs review** holds documents Cortexa wasn't sure about. They're waiting on you before they're ready to export.";
/** Their own job in Calibration (the sample is already taught, so never): why more is asked. */
const CALIBRATION_IS =
  'This job is in **Calibration**: while Cortexa learns your documents, it asks you about more of them.';
const here = (count: number, of: string) =>
  count === 1 ? `1 of ${of} is here.` : `${count.toLocaleString('en-US')} of ${of} are here.`;
/** "it", "both", "all 3": the waiting documents, as the line asks to confirm them. */
const them = (count: number) =>
  count === 1 ? 'it' : count === 2 ? 'both' : `all ${count.toLocaleString('en-US')}`;
const text = (words: string) => () => words;
// PAIGE: Paige's destinations, not the desktop's folder (`desktop-export`).
const exportIs = () =>
  hostHas('desktop-export')
    ? 'Exporting sends each document where you keep your files: a folder, Content Central, SharePoint, Google Drive, Dropbox and more.'
    : 'Exporting sends each document where you keep your files: SharePoint, Google Drive, Dropbox, Box, Content Central and more.';
// PAIGE: Paige's ways in (Paige Scan, the job's email address, a folder, files), not the desktop's computer.
const pagesFrom = () =>
  hostHas('local-storage')
    ? 'from a scanner, a folder, or files on this computer'
    : 'from a scanner, an email address, a folder, or files';
const docs = (count: number) =>
  count === 1 ? '1 document' : `${count.toLocaleString('en-US')} documents`;
const NUMBER_WORDS = [
  'no',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];
/**
 * How many sample documents there are, as the card says it: "five", until a click on the paperclip
 * or scissors (which really joins or splits them) or Add pages changes how many.
 */
const five = (count?: number) =>
  !count ? 'five' : count <= 10 ? NUMBER_WORDS[count] : count.toLocaleString('en-US');

/** PAIGE: the words on the tour's download button, counting what is there as the cards do. */
export const tourDownloadLabel = (total?: number) => `Download all ${five(total)}`;

export const TOUR_STEPS: Record<TourStepId, TourStep> = {
  // The whole pane is no control to click: Next moves on.
  pane: {
    section: 'Documents pane',
    targets: ['.document-spine', '.spine-title-row'],
    advanceOn: null,
    next: true,
    say: ({ total }) =>
      `On the left are the ${five(total)} sample documents Paige just read, one after another. A **green check** marks each one with nothing left to do.`,
    todo: text("Press Next when you've had a look."),
  },
  // Paperclip first, as the words go: one is on screen between the first two documents (owner,
  // 2026-10-05). After about four seconds the pane glides to the scissors, which sit between the
  // Summit invoice's two pages, the fifth document, so the list hasn't drawn them yet. Clicking
  // either really joins or splits the sample's documents; the later steps count what is there.
  starts: {
    section: 'Document starts',
    targets: ['.merge-boundary', '.split-boundary'],
    walkEvery: 4000,
    inTurn: true,
    spots: { '.split-boundary': '.spine-first-split' },
    showStarts: true,
    next: true,
    say: text(
      'If Paige gets a document start wrong, fix it right in the pane: the **paperclip** between two documents joins them, and the **scissors** between two pages start a new document there.',
    ),
    todo: text("Press Next when you've seen both."),
  },
  // Add pages opens a folder window (the tour's job reads a folder) and ⋯ the list of sources: the
  // step moves on once that has closed, and whatever they add really is added.
  add: {
    section: 'Adding pages',
    targets: ['.thumbnail-strip .spine-actions'],
    next: true,
    say: () =>
      `In your own jobs, new pages come in through **Add pages** at the bottom of the pane: ${pagesFrom()}.`,
    todo: text("Press Next when you're ready to move on."),
  },
  // The whole right pane for three seconds, then one value box. A clicked value lights its source on
  // the page, and the step moves on once that has been seen.
  read: {
    section: 'What it read',
    targets: ['[data-review-pane="fields"]', FIELD_VALUE],
    walkEvery: 3000,
    advanceOn: FIELD_VALUE,
    advanceAfter: 1500,
    next: true,
    say: text(
      'On the right is what it read from this document: the **document type** at the top, then each **field** and its value.',
    ),
    todo: text('Click a field value to see where it came from.'),
  },
  // One button confirms the whole document and moves on (single confirm, 2026-10-05); the card
  // names it as it reads. Clicking into a field to change its value doesn't move the step on.
  confirm: {
    section: 'Confirming',
    targets: ['.document-confirm-bar'],
    advanceOn: '.document-confirm-bar',
    next: true,
    say: text(
      "Check what it read, then confirm. Confirming tells Cortexa it got it right, and that's how it learns.",
    ),
    todo: ({ confirmLabel }) => `Try **${confirmLabel || 'Confirm & next'}** on this one.`,
  },
  review: {
    section: 'Needs review',
    targets: ['.spine-title-row'],
    next: true,
    say: ({ total }) => `${REVIEW_IS} None of these ${five(total)} need review right now.`,
    todo: text("Press Next when you've had a look."),
  },
  // Trimmed (owner, 2026-10-04): the paragraph names the list and the count; the line says what
  // to do and that the step then moves on (it does, at 0).
  // The ring stays on the review count a moment longer before the main button (owner, 2026-10-04).
  // Confirming one of several mustn't move on: the step does, a moment after the count reaches 0.
  'review-wait': {
    section: 'Needs review',
    targets: [COUNT, '.document-confirm-bar'],
    walkEvery: 3200,
    advanceOn: null,
    next: false,
    say: ({ waiting, total }) => `${REVIEW_IS} ${here(waiting, `these ${five(total)}`)}`,
    todo: ({ waiting }) => `Check and confirm ${them(waiting)} to move on.`,
    done: ({ waiting }) => waiting === 0,
  },
  export: {
    section: 'Export',
    targets: ['.export-ready button'],
    advanceOn: '.export-ready button',
    next: false,
    say: () =>
      `${exportIs()} For the tour a folder is already picked; in your own jobs, you choose where they go.`,
    todo: ({ exportLabel, total }) =>
      `Click **${exportLabel || 'Export'}** to export all ${five(total)}.`,
  },
  files: {
    section: 'Your files',
    targets: ['.caught-up-open'],
    advanceOn: '.caught-up-open',
    next: true,
    back: false,
    waitFor: '.caught-up-hero',
    whileWaiting: 'Exporting them…',
    say: ({ exported }) =>
      `All ${five(exported)} are in the folder now, each a searchable PDF with what it read saved beside it.`,
    todo: text('Click **Open folder** to see them.'),
  },
  turn: {
    section: 'Your turn',
    next: false,
    say: text(
      "That's the whole job: Paige reads, you check what it isn't sure of, and it files them. The more you teach it, the less it asks of you, until you can put your job on **Autopilot** and it takes care of everything by itself.",
    ),
  },
};

/**
 * Every way the tour on their own job differs (mockups "Tour on their own job"). Their counts;
 * the job really is set to ask for review when documents wait, and Next moves on whenever they
 * like (it is their work, not a demonstration); Export glows on the export card's own button,
 * which says Set up export until they choose where documents go; Your files opens what went
 * out from the card; the last step points at Add pages and ends with Done.
 */
export const OWN_STEPS: Partial<Record<TourStepId, Partial<TourStep>>> = {
  pane: {
    say: ({ total = 0 }) =>
      `On the left are your ${docs(total)}, one after another. A **green check** marks each one with nothing left to do.`,
  },
  add: {
    say: () => `New pages come in through **Add pages** at the bottom of the pane: ${pagesFrom()}.`,
  },
  review: {
    say: ({ calibrating }) =>
      calibrating ? CALIBRATION_IS : `${REVIEW_IS} None of yours need review right now.`,
  },
  'review-wait': {
    next: true,
    say: ({ waiting, calibrating }) =>
      `${calibrating ? CALIBRATION_IS : REVIEW_IS} ${here(waiting, 'your documents')}`,
    todo: ({ waiting }) => `Check and confirm ${them(waiting)}, or press Next.`,
  },
  // It moves on when something has gone out, however they chose to send it, not on the click.
  export: {
    targets: ['.export-ready button'],
    select: undefined,
    advanceOn: null,
    next: true,
    say: ({ exportLabel, waiting }) =>
      exportLabel === 'Set up export'
        ? `${exportIs()} This job doesn't send them anywhere yet.`
        : !exportLabel && waiting > 0
          ? `${exportIs()} Documents in **Needs review** can be exported once you confirm them.`
          : exportIs(),
    todo: ({ exportLabel }) =>
      exportLabel === 'Set up export'
        ? 'Click **Set up export** to choose where they go.'
        : exportLabel
          ? `Click **${exportLabel}** to export them.`
          : "Press Next when you're ready to move on.",
    done: ({ exported = 0 }) => exported > 0,
  },
  files: {
    targets: [],
    advanceOn: null,
    waitFor: undefined,
    openExport: true,
    back: true,
    say: ({ exported = 0, waiting }) =>
      `${exported === 1 ? '1 document is' : `${exported.toLocaleString('en-US')} documents are`} exported.${waiting > 0 ? ` The ${waiting.toLocaleString('en-US')} in **Needs review** can be exported once you confirm them.` : ''}`,
    todo: ({ folder }) =>
      folder ? 'Click **Open folder** to see them.' : 'Click **See activity** to see them.',
  },
  turn: {
    targets: ['.thumbnail-strip .spine-actions'],
    say: text(
      "That's the whole job: Paige reads, you check what it isn't sure of, and it files them. New pages come in through **Add pages**. The more you teach it, the less it asks of you, until you can put this job on **Autopilot** and it takes care of everything by itself.",
    ),
  },
};

/**
 * PAIGE: the sample tour's Export and Your files on Paige. The desktop's tour presets a folder and
 * exports on one click; Paige sends each document as soon as it is ready, to every destination the
 * job has and to Download, which every job has. So by the Export step the five are already out:
 * the step shows where export is set up (the export line's Set up export) and moves on
 * with Next, and Your files downloads the five, each a searchable PDF with what was read beside it.
 */
export const PAIGE_SAMPLE_STEPS: Partial<Record<TourStepId, Partial<TourStep>>> = {
  export: {
    // The export line above the documents list: how many of the five are exported (paige/HostExportPanel.tsx).
    targets: ['.export-ready'],
    advanceOn: undefined,
    next: true,
    say: () =>
      `${exportIs()} Paige sends each one the moment it's ready. For the tour they stay in Paige, ready to download; in your own jobs, you choose where they go.`,
    todo: text("Press Next when you've had a look."),
  },
  files: {
    targets: ['.tour-download'],
    advanceOn: undefined,
    waitFor: undefined,
    // Until every one of the five has its package the card says Exporting them… (TourDownload.tsx).
    download: true,
    back: true,
    say: ({ total }) =>
      `All ${five(total)} are exported, each a searchable PDF with what Cortexa read saved beside it.`,
    todo: ({ total }) => `Click **${tourDownloadLabel(total)}** to see them.`,
  },
};

/**
 * PAIGE: the own-job tour's Export and Your files on Paige. The glow lands on the export line
 * (`.export-ready`), which in Paige is drawn from its destinations (paige/HostExportPanel.tsx), and Your
 * files was always skipped because what went out was read from the desktop's export record. Paige sends
 * each document the moment it is ready, so Export shows the job's export line above the documents
 * list (Set up export while only Download is there, Sends as soon as ready once a destination is),
 * and moves on with Next; Your files is shown once some of the job's documents are delivered
 * (the documents with their package, TourCard's useOwnExport) and opens the job's Export, where
 * the destinations and Download are.
 */
export const PAIGE_OWN_STEPS: Partial<Record<TourStepId, Partial<TourStep>>> = {
  export: {
    targets: ['.export-ready'],
    done: undefined,
    say: ({ exportLabel, waiting }) =>
      exportLabel === 'Set up export'
        ? `${exportIs()} This job keeps them in Paige, ready to download; it doesn't send them anywhere else yet.`
        : waiting > 0
          ? `${exportIs()} Paige sends each one the moment it's ready; the ones in **Needs review** go once you confirm them.`
          : `${exportIs()} Paige sends each one to this job's destinations the moment it's ready.`,
    todo: ({ exportLabel }) =>
      exportLabel === 'Set up export'
        ? 'Click **Set up export** to choose where they go, or press Next.'
        : "Press Next when you've had a look.",
  },
  files: {
    todo: text('Click **Open Export** to see where they went and download them.'),
  },
};

/** The step as the card shows it: the sample tour's, or with their own job's differences. */
export const tourStep = (id: TourStepId, own = false): TourStep =>
  own
    ? hostHas('desktop-export')
      ? { ...TOUR_STEPS[id], ...OWN_STEPS[id] }
      : { ...TOUR_STEPS[id], ...OWN_STEPS[id], ...PAIGE_OWN_STEPS[id] }
    : hostHas('desktop-export')
      ? TOUR_STEPS[id]
      : { ...TOUR_STEPS[id], ...PAIGE_SAMPLE_STEPS[id] };

/** The controls whose click moves the step on (TourStep.advanceOn), or null when none does. */
export const advanceOnOf = (step: TourStep): string | null =>
  step.advanceOn !== undefined
    ? step.advanceOn
    : step.next && step.targets?.length
      ? step.targets.join(', ')
      : null;

/** The nine steps, in order. When something waits, Needs review is "review-wait" in the same place. */
export const TOUR_ORDER: TourStepId[] = [
  'pane',
  'starts',
  'add',
  'read',
  'confirm',
  'review',
  'export',
  'files',
  'turn',
];
const placeOf = (step: TourStepId) => TOUR_ORDER.indexOf(step === 'review-wait' ? 'review' : step);

/**
 * `own`: the tour runs on the person's own job, which leaving or finishing never removes.
 * `ready`: the tour has begun. The sample tour begins when the person presses Start the tour in
 * the window that shows while the five are read (TourStart); a tour on their own job begins at once.
 * `from`: where the sample tour was started, which Leave tour goes back to (see tourReturn).
 */
export type TourState = {
  jobId: string;
  step: TourStepId;
  startedAt: string;
  own?: boolean;
  ready?: boolean;
  from?: TourOrigin;
};

/** Where the person is: the card's bar shows it, and its name tells screen readers "step 7 of 10". */
export const tourPosition = (step: TourStepId) => ({
  index: placeOf(step),
  count: TOUR_ORDER.length,
});

/**
 * The step after this one. Needs review takes the wording for what waits when the step opens.
 * On their own job, Your files is shown only when something went out.
 */
export function nextTourStep(
  step: TourStepId,
  context: TourContext,
  own = false,
): TourStepId | null {
  const next = TOUR_ORDER[placeOf(step) + 1];
  if (!next) return null;
  if (own && next === 'files' && !(context.exported && context.exported > 0)) return 'turn';
  return next === 'review' && context.waiting > 0 ? 'review-wait' : next;
}

/**
 * The step before this one, for Back: Needs review in the wording for what waits now, and on their
 * own job Export again when nothing went out (Your files was not shown). Null on the first step.
 */
export function previousTourStep(
  step: TourStepId,
  context: TourContext,
  own = false,
): TourStepId | null {
  const previous = TOUR_ORDER[placeOf(step) - 1];
  if (!previous) return null;
  if (own && previous === 'files' && !(context.exported && context.exported > 0)) return 'export';
  return previous === 'review' && context.waiting > 0 ? 'review-wait' : previous;
}

/** Whether the card offers Back: every step but the first, unless going back would say something no longer true. */
export const canGoBack = (step: TourStepId, own = false) =>
  placeOf(step) > 0 && tourStep(step, own).back !== false;

/**
 * Whether their first own job offers the tour (owner, 2026-10-04): once, when that job has
 * finished reading, to someone new (no export yet) who hasn't taken the tour to its end, and
 * never on a sample job or while a tour runs.
 */
export function shouldOfferOwnTour({
  tour,
  sample,
  reading,
  documents,
  answered,
  taken,
  exportedBefore,
}: {
  tour: TourState | null;
  sample: boolean;
  reading: boolean;
  documents: number;
  answered: boolean;
  taken: boolean;
  exportedBefore: boolean;
}): boolean {
  return !tour && !sample && !reading && documents > 0 && !answered && !taken && !exportedBefore;
}

/** The offer is made once; the sample tour counts as taken once it reaches Your turn. */
export const TOUR_OFFER_KEY = 'cpv6-own-tour-offer';
export const TOUR_TAKEN_KEY = 'cpv6-tour-taken';
/**
 * Set once this person has taken a tour, to its last step or leaving part-way (Brandy, 2026-10-05:
 * "Take a 2-minute tour" right after it read as weird). TOUR_TAKEN_KEY is only the sample tour's end,
 * which decides the offer on their first job; this one only changes the words that offer it.
 */
export const TOUR_WATCHED_KEY = 'cpv6-tour-watched';
/**
 * Set once either tour, the sample's or one on their own job, has reached its last step (Your turn). Only telemetry
 * reads it: a tour finished on their own job is "finished", not "left", while TOUR_TAKEN_KEY stays the sample's end.
 */
export const TOUR_FINISHED_KEY = 'cpv6-tour-finished';
/** Where the tour stands as the main window opens (telemetry `TelemetrySessionContext.tour`), from this computer's flags. */
export function sessionTourState(
  storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage,
): 'running' | 'finished' | 'left' | 'none' {
  if (readTour(storage)) return 'running';
  if (readTourFlag(TOUR_FINISHED_KEY, storage) || readTourFlag(TOUR_TAKEN_KEY, storage))
    return 'finished';
  return readTourFlag(TOUR_WATCHED_KEY, storage) ? 'left' : 'none';
}
/** What the places that offer the tour call it: their own words the first time, then "Watch the tour again". */
export const tourOfferLabel = (first: string, watched: boolean = readTourFlag(TOUR_WATCHED_KEY)) =>
  watched ? 'Watch the tour again' : first;
export function readTourFlag(
  key: string,
  storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage,
): boolean {
  try {
    return Boolean(storage?.getItem(key));
  } catch {
    return false;
  }
}
export function markTourFlag(
  key: string,
  storage: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage,
): void {
  try {
    storage?.setItem(key, new Date().toISOString());
  } catch {
    /* Only means the offer could be made again. */
  }
}

// ---------------------------------------------------------------------------
// Where the tour is: one record on this computer, announced to every component
// ---------------------------------------------------------------------------

export const TOUR_STORAGE_KEY = 'cpv6-demo-tour';
export const TOUR_EVENT = 'capturepoint-tour';

export function readTour(
  storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage,
): TourState | null {
  try {
    const raw = storage?.getItem(TOUR_STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<TourState>;
    // A tour saved on the Teach step (gone with the filters, 2026-10-30) carries on at the step after it.
    if ((value.step as string) === 'teach') value.step = 'read';
    if (typeof value.jobId !== 'string' || !value.step || !(value.step in TOUR_STEPS)) return null;
    const from = parseTourOrigin(value.from);
    return {
      jobId: value.jobId,
      step: value.step,
      startedAt: String(value.startedAt ?? ''),
      ...(value.own === true ? { own: true } : {}),
      ...(value.ready === true ? { ready: true } : {}),
      ...(from ? { from } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Tour jobs on their way out. An exported job keeps recovery copies, and the engine refuses to
 * delete it until its cleanup has removed them (a sweep every 20 s; measured October 4: about
 * two minutes). Until then the job stays off every list, and a restart picks the removal up.
 */
export const TOUR_REMOVALS_KEY = 'cpv6-tour-removals';

export function readTourRemovals(
  storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage,
): string[] {
  try {
    const value = JSON.parse(storage?.getItem(TOUR_REMOVALS_KEY) ?? '[]');
    return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export function markTourRemoval(
  jobId: string,
  removing: boolean,
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined = globalThis.localStorage,
): void {
  const others = readTourRemovals(storage).filter((id) => id !== jobId);
  try {
    storage?.setItem(TOUR_REMOVALS_KEY, JSON.stringify(removing ? [...others, jobId] : others));
  } catch {
    /* A blocked store only means a restart shows the job again; it can be deleted by hand. */
  }
}

/**
 * How the tour came to a new step, for telemetry: Next on the card, the step's own control used (the filter, the
 * value, Confirm, Export, Open folder), the step moving on by itself (Needs review reaching 0, something exported
 * on their own job), or Back.
 */
export type TourVia = 'next' | 'control' | 'auto' | 'back';
type TourWriteListener = (state: TourState | null, via?: TourVia) => void;
let tourWriteListener: TourWriteListener | null = null;
/**
 * Hears every write of the tour's place (tour-telemetry.ts, which reports the steps). Set from outside, so this
 * file keeps no runtime imports; a listener that throws never stops the tour.
 */
export function onTourWrite(listener: TourWriteListener | null): void {
  tourWriteListener = listener;
}

export function writeTour(
  state: TourState | null,
  storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined = globalThis.localStorage,
  via?: TourVia,
): void {
  try {
    if (state) storage?.setItem(TOUR_STORAGE_KEY, JSON.stringify(state));
    else storage?.removeItem(TOUR_STORAGE_KEY);
  } catch {
    // A blocked store only loses the tour's place; the workspace works without it.
  }
  try {
    tourWriteListener?.(state, via);
  } catch {
    /* Telemetry never stops the tour. */
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(TOUR_EVENT));
}

// ---------------------------------------------------------------------------
// Where the person was when the tour started, and the way back there
// ---------------------------------------------------------------------------

/**
 * Where a sample tour was started (Brandy, 2026-10-05: she took it from Settings while in her job
 * Testy, left while the five were read, and landed in Scan jobs and New job instead of Testy). The
 * job and, where one was open, its document. `screen` is Scan jobs when that
 * was in front; anything else (Settings, the job itself, New job opened over it) goes back to the job.
 */
export type TourOrigin = { jobId: string; screen: 'job' | 'jobs'; documentId?: string };

export function parseTourOrigin(value: unknown): TourOrigin | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { jobId, screen, documentId } = value as Record<string, unknown>;
  if (typeof jobId !== 'string' || !jobId) return undefined;
  return {
    jobId,
    screen: screen === 'jobs' ? 'jobs' : 'job',
    ...(typeof documentId === 'string' && documentId ? { documentId } : {}),
  };
}

/** The document open in each job's workspace, kept as it changes (this window only). */
const openDocuments = new Map<string, { documentId: string }>();
export function noteOpenDocument(jobId: string, documentId: string): void {
  openDocuments.set(jobId, { documentId });
}
export const openDocumentOf = (jobId: string) => openDocuments.get(jobId);

/** Where a tour started now goes back to: this job (with its open document) on this screen, or nowhere. */
export function tourOriginOf(jobId: string | undefined, screen: string): TourOrigin | undefined {
  if (!jobId) return undefined;
  const open = openDocumentOf(jobId);
  return {
    jobId,
    screen: screen === 'jobs' ? 'jobs' : 'job',
    ...(open ? { documentId: open.documentId } : {}),
  };
}

/**
 * Where Leave tour goes: back where the tour was started, when that job is still there; otherwise
 * (started from the welcome, or the job deleted meanwhile) the name step of My own documents.
 */
export type TourReturn =
  { to: 'job' | 'jobs'; jobId: string; documentId?: string } | { to: 'new-job' };
export function tourReturn(from: TourOrigin | undefined, jobIds: readonly string[]): TourReturn {
  if (!from || !jobIds.includes(from.jobId)) return { to: 'new-job' };
  if (from.screen === 'jobs') return { to: 'jobs', jobId: from.jobId };
  return {
    to: 'job',
    jobId: from.jobId,
    ...(from.documentId ? { documentId: from.documentId } : {}),
  };
}

/** The document to open again, handed once to that job's workspace, which opens it when its list is in. */
type DocumentReturn = { jobId: string; documentId: string };
let documentReturn: DocumentReturn | null = null;
export function requestTourReturn(request: DocumentReturn | null): void {
  documentReturn = request;
}
/** PAIGE: the document asked for, left where it is: a mount that does not hold it yet must not take it. */
export const tourReturnDocument = (jobId: string): string | null =>
  documentReturn?.jobId === jobId ? documentReturn.documentId : null;
export function takeTourReturn(jobId: string): DocumentReturn | null {
  if (documentReturn?.jobId !== jobId) return null;
  const taken = documentReturn;
  documentReturn = null;
  return taken;
}

// ---------------------------------------------------------------------------
// The window while the five are read: documents only
// ---------------------------------------------------------------------------

/**
 * The engine's step in the tour's window, without page or file counts (Brandy, 2026-10-05: the
 * window said "Reading page 0 of 6" beside "0 of 5 read": "is it five? six?"). Its count is documents.
 */
const READING_STEPS: Record<string, string> = {
  preparing: 'Getting the documents ready',
  recognizing: 'Reading the documents',
  organizing: 'Finding where each document starts',
  understanding: 'Reading the values',
  finalizing: 'Finishing up',
};
export const tourReadingStep = (stage: string | null | undefined): string | null =>
  (stage && READING_STEPS[stage]) || null;
export const tourReadCount = (read: number, total: number) =>
  `${read.toLocaleString('en-US')} of ${total.toLocaleString('en-US')} ${total === 1 ? 'document' : 'documents'}`;

const COUNT_WORDS = [
  '',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];
/**
 * PAIGE: the start window's ready line, whole whatever is missing. It was put together in the window
 * from the live list and said "Paige read all no in 4 seconds" when that list had gone (first-run
 * walk, 2026-10-06). No count: "the sample documents"; one: "the one"; no time: no time; one second:
 * "1 second".
 */
export function tourReadyLine(count: number, where = '', seconds?: number | null): string {
  const what =
    !Number.isFinite(count) || count < 1
      ? 'the sample documents'
      : count === 1
        ? 'the one'
        : `all ${COUNT_WORDS[count] ?? count.toLocaleString('en-US')}`;
  const took =
    seconds && Number.isFinite(seconds) && seconds > 0
      ? ` in ${seconds === 1 ? '1 second' : `${Math.round(seconds).toLocaleString('en-US')} seconds`}`
      : '';
  return `Paige read ${what}${where}${took}.`;
}

/**
 * PAIGE: the most documents the start window has shown read for a job. The window only ever counts
 * up: a list that reports fewer read than it did (the batch read a second time) keeps what was shown.
 */
export function tourReadPeak(
  peak: { jobId: string; types: (string | null)[] },
  jobId: string,
  types: (string | null)[],
): { jobId: string; types: (string | null)[] } {
  if (peak.jobId !== jobId) return { jobId, types };
  const read = (list: (string | null)[]) => list.filter(Boolean).length;
  return read(types) < read(peak.types) ? peak : { jobId, types };
}

// not-read.ts's isReaderProblem, repeated here so this file keeps no runtime imports (its tests load it alone).
const isReaderProblem = (cause: string | null | undefined): cause is ReaderProblem =>
  cause === 'field-reader-missing' || cause === 'field-reader-broken';

type TourDocument = {
  notReadReason?: string | null;
  notReadCause?: string | null;
  documentType?: string | null;
  documentTypeVerified?: boolean | null;
};

/**
 * One document in the window: its type once its reading truly finished, else null (being read, being
 * read again, or waiting for the field reader). The engine reports each document as its values are in
 * (its "understanding" event carries the finished document), and the workspace adds it to the list
 * then, so the count goes up one document at a time (Brandy, October 5: it sat at "0 of 5 documents"
 * until all five finished at once, because the reading screen counted none of them).
 */
export const tourDocumentType = (document: TourDocument, stillReading: boolean): string | null =>
  document.notReadReason ||
  (stillReading &&
    /^unclassified/i.test(document.documentType ?? '') &&
    !document.documentTypeVerified)
    ? null
    : document.documentType || 'Document';

/** Documents the engine reads again by itself (a first reading that didn't finish). */
export const tourReadAgain = (documents: readonly TourDocument[]): number =>
  documents.filter((document) => document.notReadReason && !isReaderProblem(document.notReadCause))
    .length;

/** The field reader can't read (not-read.ts): the window says so, with the exact error, instead of promising a read. */
export const tourReaderProblem = (
  documents: readonly TourDocument[],
): { cause: ReaderProblem; detail: string } | null => {
  // PAIGE: the reader is Paige's own, never installed here; its window keeps counting (`not-read-retry`).
  if (!hostHas('not-read-retry')) return null;
  const waiting = documents.find(
    (document) => document.notReadReason && isReaderProblem(document.notReadCause),
  );
  return waiting && isReaderProblem(waiting.notReadCause)
    ? { cause: waiting.notReadCause, detail: waiting.notReadReason ?? '' }
    : null;
};
